import { twMerge } from "tailwind-merge";
import { useChatPersonas } from "../ChatContext";
import { MessageLockup } from "./MessageLockup";
import ReactMarkdown from "react-markdown";
import type { ReactNode } from "react";
import type { AIMessage as SDKAIMessage, Message } from "@langchain/langgraph-sdk";
import { extractText, type MessageContent } from "../attachments/types";
import {
  stripJsonBlocks as stripJsonBlocksImpl,
  hideIncompleteImageMarkdown,
} from "./streamingMarkdown";

// ─────────────────────────────────────────────────────────────────────────
// Compound parts (escape hatch for full customization)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Pull just the keys the ToolCallIndicator labels off (operation_id,
 * resource, q.query) from a tool_use input string that's still streaming.
 *
 * Why not a JSON parser? Two reasons:
 *
 *   1. The input is a partial JSON string for most of the streaming
 *      window (`{"operation_id": "uploads.fr` — unbalanced quote/brace).
 *      A real parser throws; we'd have to either bring in a partial-JSON
 *      library or write a tolerant one. Both are heavy for what the
 *      indicator actually needs.
 *   2. LangChain's `tool_calls[i].args` would normally be that parsed
 *      view, populated as soon as the JSON is parseable. In practice
 *      the langgraph stream forwards `tool_calls[i].args` as `{}` for
 *      the entire mid-stream window even though the underlying
 *      `content[].input` STRING has the data — so reading `tool_calls`
 *      alone leaves the indicator stuck on its "Working on it"
 *      placeholder until the call completes. The raw input string is
 *      the truth.
 *
 * The indicator only needs three keys to label every MCP tool. Pulling
 * them with anchored regex on the streaming string gives us a meaningful
 * label the moment each key's value is even partially streamed in.
 *
 * Returned values may themselves be partial substrings (e.g. `"uploads.fr"`
 * while the operation_id is still streaming). That's fine — the
 * indicator's resource/operation fallback chain handles unknown values
 * by humanizing them.
 */
function extractArgsFromPartialInput(
  input: string
): Record<string, unknown> | undefined {
  if (!input.trim()) return undefined;
  // Try a full parse first — once the JSON is complete, that's
  // strictly better than regex (preserves nested structure for any
  // future label logic that wants it).
  try {
    const parsed = JSON.parse(input);
    if (parsed && typeof parsed === "object") return parsed;
  } catch {
    // Streaming partial — fall through to regex extraction.
  }
  // Closed value first; else the OPEN TAIL — the value still streaming
  // at the end of the string. Without the tail form, a long value (a
  // task `description` is a multi-paragraph prompt) shows NOTHING for
  // the entire seconds-long window until its closing quote arrives —
  // and every key behind it in the schema is blocked too. Capturing the
  // tail makes labels appear with the first streamed characters.
  const matchStringValue = (key: string): string | undefined => {
    const closed = input.match(new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`));
    if (closed) return closed[1];
    const openTail = input.match(new RegExp(`"${key}"\\s*:\\s*"([^"]*)$`));
    return openTail ? openTail[1] : undefined;
  };

  const result: Record<string, unknown> = {};
  const op = matchStringValue("operation_id");
  if (op !== undefined) result.operation_id = op;
  const res = matchStringValue("resource");
  if (res !== undefined) result.resource = res;
  const query = matchStringValue("query");
  if (query !== undefined) result.q = { query };
  // Subject keys for the ToolActivity feed rows: file tools carry
  // file_path/path, glob/grep carry pattern, task carries
  // description + subagent_type.
  const filePath = matchStringValue("file_path");
  if (filePath !== undefined) result.file_path = filePath;
  const path = matchStringValue("path");
  if (path !== undefined) result.path = path;
  const pattern = matchStringValue("pattern");
  if (pattern !== undefined) result.pattern = pattern;
  const description = matchStringValue("description");
  if (description !== undefined) result.description = description;
  const subagent = matchStringValue("subagent_type");
  if (subagent !== undefined) result.subagent_type = subagent;
  return Object.keys(result).length > 0 ? result : undefined;
}

// Structured-output calls (langchain's `toolStrategy`) force a tool call
// whose name langchain auto-generates as `extract-<n>` (an incrementing
// integer — see langchain agents/responses.js getFunctionName). These are
// pure internal data-extraction calls (NameProjectService, recommendDomains,
// generateInsights, …) that the user should never see as a tool-call
// indicator. The langgraph-ai-sdk stream path already drops them
// (stream.ts: /^extract/); this is the matching guard for the raw
// content-block render path here, which reads tool_use blocks directly.
// Anchored to the `-<digits>` suffix so a real tool named `extract_*`
// would still surface. Case-insensitive because the raw name (`extract-2`)
// is title-cased to `Extract-2` for display, and to tolerate either form.
const INTERNAL_TOOL_NAME = /^extract-\d+$/i;

function isInternalToolName(name: string): boolean {
  return INTERNAL_TOOL_NAME.test(name);
}

// Exported for unit tests. The read-order priority below is the actual
// contract this function ships — pinning it in tests keeps a future
// refactor from regressing the indicator to the "Working on it"
// streaming-stuck state we hit once.
export function extractToolCalls(
  message: Message
): { id: string; name: string; args?: Record<string, unknown> }[] {
  // Read order is load-bearing for streaming UX.
  //
  // LangChain exposes three tool-call shapes on an AIMessage. In the
  // langgraph stream, only ONE actually grows mid-stream:
  //
  //   1. `content[].input` (Anthropic content block) — a growing JSON
  //      STRING. This is the source of truth during streaming: every
  //      `messages/partial` event arrives with more characters of the
  //      JSON. THIS IS WHAT THE INDICATOR NEEDS.
  //   2. `message.tool_calls[i].args` — would normally be the parsed
  //      object form of (1). In practice the stream pipeline holds it
  //      at `{}` for the entire mid-stream window (only the final,
  //      post-call snapshot has populated args). Reading this gives
  //      "Working on it" until the tool completes, at which point the
  //      indicator is filtered out by completedToolCallIds — net
  //      effect: the indicator NEVER sees real args.
  //   3. `message.tool_call_chunks` — partial JSON as a string,
  //      identical content to (1). Redundant.
  //
  // So we read (1) and parse what we need with anchored regex (see
  // extractArgsFromPartialInput above). When (1) is a fully-parseable
  // JSON the regex path still works, but we prefer the real parser.
  //
  // Per-block fallback: on SETTLED messages (persisted checkpoints,
  // history reload) the pipeline inverts — content blocks carry
  // `input: ""` while `tool_calls[i].args` holds the full parsed
  // object. Preferring content wholesale would strand every settled
  // row without args, so each block falls back to its tool_calls
  // sibling (matched by id) and we keep whichever side knows more.
  const parsedArgsByToolCallId = new Map<string, Record<string, unknown>>();
  const rawToolCalls = (message as any).tool_calls as
    | Array<{ id?: string; args?: unknown }>
    | undefined;
  if (Array.isArray(rawToolCalls)) {
    for (const tc of rawToolCalls) {
      if (
        tc.id &&
        tc.args &&
        typeof tc.args === "object" &&
        !Array.isArray(tc.args)
      ) {
        parsedArgsByToolCallId.set(tc.id, tc.args as Record<string, unknown>);
      }
    }
  }

  const content = message.content;
  if (Array.isArray(content)) {
    const fromContent = content
      .filter(
        (block: any) =>
          block.type === "tool_use" &&
          block.id &&
          block.name &&
          !isInternalToolName(block.name)
      )
      .map((block: any) => {
        const rawInput = block.input;
        // TEMP INSTRUMENTATION (choppy-label diagnosis): is a streaming
        // file-tool's input a growing STRING (server streams tokens; label
        // should resolve fast) or a partial OBJECT (our length-fingerprint
        // can't see it grow → label frozen till settle)? Logs the shape +
        // whether file_path is present yet, on every partial. Remove after.
        if (block.name === "edit_file" || block.name === "write_file" || block.name === "read_file") {
          const kind = typeof rawInput;
          const detail =
            kind === "string"
              ? { len: rawInput.length, head: rawInput.slice(0, 80), hasPathKey: rawInput.includes('"file_path"') }
              : kind === "object" && rawInput
                ? { keys: Object.keys(rawInput), file_path: (rawInput as any).file_path }
                : { value: rawInput };
          // eslint-disable-next-line no-console
          console.debug("[toolinput]", block.name, block.id, kind, detail);
        }
        let args: Record<string, unknown> | undefined;
        if (typeof rawInput === "string") {
          args = extractArgsFromPartialInput(rawInput);
        } else if (rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)) {
          args = rawInput as Record<string, unknown>;
        }
        const parsed = parsedArgsByToolCallId.get(block.id);
        if (
          parsed &&
          Object.keys(parsed).length > Object.keys(args ?? {}).length
        ) {
          args = parsed;
        }
        return { id: block.id, name: block.name, args };
      });
    if (fromContent.length > 0) return fromContent;
  }

  // Fallback: read from `message.tool_calls` for non-streaming snapshots
  // (thread history reload, persisted state) where there's no content
  // block array but the parsed tool_calls is canonical.
  const fromToolCalls = (message as any).tool_calls as
    | Array<{ id?: string; name?: string; args?: unknown }>
    | undefined;
  if (Array.isArray(fromToolCalls) && fromToolCalls.length > 0) {
    return fromToolCalls
      .filter((tc) => tc.id && tc.name && !isInternalToolName(tc.name))
      .map((tc) => ({
        id: tc.id!,
        name: tc.name!,
        args:
          tc.args && typeof tc.args === "object" && !Array.isArray(tc.args)
            ? (tc.args as Record<string, unknown>)
            : undefined,
      }));
  }

  return [];
}

export interface AIMessageRootProps {
  children: ReactNode;
  /** Follow-on message in the same turn — no avatar/name, same column. */
  continuation?: boolean;
  className?: string;
}

function Root({ children, continuation, className }: AIMessageRootProps) {
  const { assistant } = useChatPersonas();
  return (
    <MessageLockup
      persona={assistant}
      tone="assistant"
      continuation={continuation}
      testId="ai-message"
      className={className}
    >
      {children}
    </MessageLockup>
  );
}

export interface AIMessageContentProps {
  content?: MessageContent;
  children?: ReactNode;
  state?: "active" | "inactive" | "loading";
  /** Whether to render JSON code blocks. Default: true */
  showJSON?: boolean;
  className?: string;
}

// Re-export for backward compatibility with any external imports. The
// implementation now lives in ./streamingMarkdown.ts alongside its
// sibling `hideIncompleteImageMarkdown` and is unit-tested there.
const stripJsonBlocks = stripJsonBlocksImpl;

/**
 * Prepare streaming text for ReactMarkdown rendering.
 *
 *   1. Optionally strip JSON code fences (e.g. structured-output spew
 *      we don't want the user to see). Opt-in via `showJSON: false`.
 *   2. Always hide an incomplete trailing markdown image. Without this
 *      the user sees the raw `![alt](https://...` typing out token by
 *      token before the closing `)` arrives and ReactMarkdown can
 *      render it as an `<img>`.
 *
 * Both transforms are conservative — only the LAST unclosed fragment
 * is stripped; earlier complete content is preserved verbatim.
 */
function prepareStreamingText(text: string, showJSON: boolean): string {
  let result = text;
  if (!showJSON) result = stripJsonBlocks(result);
  result = hideIncompleteImageMarkdown(result);
  return result;
}

function Content({ content, children, state = "active", showJSON = true, className }: AIMessageContentProps) {
  let rendered: ReactNode;
  if (content !== undefined) {
    let text = typeof content === "string" ? content : extractText(content);
    if (text) text = prepareStreamingText(text, showJSON);
    rendered = text ? <ReactMarkdown>{text}</ReactMarkdown> : null;
  } else if (typeof children === "string") {
    let text = prepareStreamingText(children, showJSON);
    rendered = text ? <ReactMarkdown>{text}</ReactMarkdown> : null;
  } else {
    rendered = children;
  }

  if (!rendered) return null;

  return (
    <div
      data-role="assistant"
      className={twMerge(
        "text-sm prose prose-sm max-w-none text-base-600 break-words",
        state === "inactive" && "text-base-500",
        className
      )}
    >
      {rendered}
    </div>
  );
}

export interface AIMessageBubbleProps {
  children: ReactNode;
  className?: string;
}

function Bubble({ children, className }: AIMessageBubbleProps) {
  return (
    <div className={twMerge("bg-base-200 rounded-2xl px-4 py-3 max-w-[80%]", className)}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Default component — takes a Message, renders everything
// ─────────────────────────────────────────────────────────────────────────

export interface AIMessageProps {
  /** The AI message to render */
  message: SDKAIMessage;
  /** Whether this message is actively streaming */
  isStreaming?: boolean;
  /** Follow-on message in the same turn — no avatar/name, same column. */
  continuation?: boolean;
  /** Whether to render JSON code blocks. Default: true */
  showJSON?: boolean;
  /** Additional content rendered after the message (e.g. intent buttons) */
  children?: ReactNode;
  className?: string;
}

/**
 * Renders an AI message's text content. Tool activity is the
 * ToolActivity card's job — consumers derive groups with
 * `useToolActivity(messages)` and render `<Chat.ToolActivity>` at each
 * group's anchor message, as a sibling of this component. Tool-only
 * messages (no visible text) render null here so the card is the only
 * artifact of the burst.
 *
 * @example Basic usage
 * ```tsx
 * const activity = useToolActivity(messages);
 * <Chat.AIMessage message={msg} isStreaming={isActive} />
 * {msg.id && activity.get(msg.id) && (
 *   <Chat.ToolActivity group={activity.get(msg.id)!} isStreaming={isActive} />
 * )}
 * ```
 *
 * @example Full customization (escape hatch)
 * ```tsx
 * <Chat.AIMessage.Root>
 *   <Chat.AIMessage.Content content={msg.content} />
 * </Chat.AIMessage.Root>
 * ```
 */
function AIMessageComponent({
  message,
  isStreaming = false,
  continuation = false,
  showJSON = true,
  children,
  className,
}: AIMessageProps) {
  let textContent = extractText(message.content);
  if (!showJSON && textContent) textContent = stripJsonBlocks(textContent);

  if (!textContent?.trim() && !children) return null;

  return (
    <Root continuation={continuation} className={className}>
      {textContent?.trim() && (
        <Content
          content={message.content}
          showJSON={showJSON}
          state={isStreaming ? "active" : "inactive"}
        />
      )}
      {children}
    </Root>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Attach compound parts to the component
// ─────────────────────────────────────────────────────────────────────────

export const AIMessage = Object.assign(AIMessageComponent, {
  Root,
  Content,
  Bubble,
});
